import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import Button from 'components/Button';
import ExternalLink from 'components/ExternalLink';
import UppercaseText from 'components/UppercaseText';
import RadioButton from 'components/RadioButton';
import {formatDate} from 'utils/datetime';

import {
  MODALS,
  fetchGocardlessCustomerAndInvitation,
  attachGoCardlessCustomerToBooking,
  attachGoCardlessInvitationToBooking
} from 'ducks/tenancy';

import {closeModal} from 'ducks/modals';

import hyperlink from './assets/web-hyperlink.svg';

import './styles.css';

const GOCARDLESS_SUGGESTED_RADIO_BUTTON = 'gocardlessSuggested';

interface CustomerRowProps {
  customer: {
    id: number;
    name: string;
    email: string;
    status: string;
    createdAt: string;
    gocardlessUrl: string;
    canBeAttachedToBooking: boolean;
  };
  isSelected: boolean;
  onSelect: Function;
}

const CustomerRow = ({customer, isSelected, onSelect}: CustomerRowProps) => (
  <div
    className={classNames('CustomerRow row', 'colab-centered-v', {
      inactive: !customer.canBeAttachedToBooking
    })}>
    <div className="name">
      <RadioButton
        name={GOCARDLESS_SUGGESTED_RADIO_BUTTON}
        value={customer.id.toString()}
        checked={isSelected}
        disabled={!customer.canBeAttachedToBooking}
        onChange={() =>
          customer.canBeAttachedToBooking && onSelect(customer.id)
        }
      />
      <div className="colab-bold">{customer.name}</div>
    </div>
    <div className="status">Mandate {customer.status}</div>
    <div className="email">{customer.email}</div>
    <div className="date">Created {formatDate(customer.createdAt)}</div>
    <div className="link colab-clickable">
      <ExternalLink href={customer.gocardlessUrl} targetBlank>
        <img src={hyperlink} alt="GoCardless customer link" />
      </ExternalLink>
    </div>
  </div>
);

interface InvitationRowProps {
  invitation: {
    id: number;
    name: string;
    email: string;
    status: string;
    sentAt: string;
    gocardlessUrl: string;
  };
  isSelected: boolean;
  onSelect: Function;
}

const InvitationRow = ({
  invitation,
  isSelected,
  onSelect
}: InvitationRowProps) => (
  <div className="InvitationRow row colab-centered-v">
    <div className="name">
      <RadioButton
        name={GOCARDLESS_SUGGESTED_RADIO_BUTTON}
        value={invitation.id.toString()}
        checked={isSelected}
        onChange={() => onSelect(invitation.id)}
      />
      <div className="colab-bold">{invitation.name}</div>
    </div>
    <div className="status">{invitation.status} Invite</div>
    <div className="email">{invitation.email}</div>
    <div className="date">Sent {formatDate(invitation.sentAt)}</div>
    <div className="link colab-clickable">
      <ExternalLink href={invitation.gocardlessUrl} targetBlank>
        <img src={hyperlink} alt="GoCardless invite link" />
      </ExternalLink>
    </div>
  </div>
);

const CUSTOMER_TYPE = 'customer';
const INVITATION_TYPE = 'invitation';

class Suggested extends React.Component<any> {
  state = {
    selectedRow: {type: null, id: null}
  };

  selectCustomer = id => {
    this.setState({selectedRow: {type: CUSTOMER_TYPE, id}});
  };

  selectInvitation = id => {
    this.setState({selectedRow: {type: INVITATION_TYPE, id}});
  };

  attachGoCardlessCustomerToBooking = customerId => {
    const {tenancyId} = this.props;

    const extraActions = [
      () => closeModal(MODALS.SETUP_GOCARDLESS),
      () => fetchGocardlessCustomerAndInvitation({tenancyId})
    ];

    this.props.attachGoCardlessCustomerToBooking(
      {tenancyId, goCardlessCustomer: customerId},
      extraActions
    );
  };

  attachGoCardlessInvitationToBooking = invitationId => {
    const {tenancyId} = this.props;

    const extraActions = [
      () => closeModal(MODALS.SETUP_GOCARDLESS),
      () => fetchGocardlessCustomerAndInvitation({tenancyId})
    ];

    this.props.attachGoCardlessInvitationToBooking(
      {tenancyId, goCardlessInvitation: invitationId},
      extraActions
    );
  };

  attachCustomerOrInvitation = () => {
    const {selectedRow: {type, id}} = this.state;

    if (type === CUSTOMER_TYPE) {
      this.attachGoCardlessCustomerToBooking(id);
    }

    if (type === INVITATION_TYPE) {
      this.attachGoCardlessInvitationToBooking(id);
    }
  };

  render() {
    const {customers, invitations} = this.props;
    const {selectedRow} = this.state;
    const showCustomersAndInvitations =
      !_.isEmpty(customers) || !_.isEmpty(invitations);

    return (
      <div className="Suggested">
        <div className="contentContainer">
          <UppercaseText>
            Suggested GoCardless invites and mandates
          </UppercaseText>
          {showCustomersAndInvitations && (
            <div className="content colab-medium-border-radius">
              {_.map(invitations, invitation => (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  onSelect={this.selectInvitation}
                  isSelected={
                    selectedRow.type === INVITATION_TYPE &&
                    selectedRow.id === invitation.id
                  }
                />
              ))}
              {_.map(customers, customer => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  onSelect={this.selectCustomer}
                  isSelected={
                    selectedRow.type === CUSTOMER_TYPE &&
                    selectedRow.id === customer.id
                  }
                />
              ))}
            </div>
          )}
        </div>
        <div className="actionButton">
          <Button
            blue
            onClick={this.attachCustomerOrInvitation}
            disabled={_.isNil(selectedRow.id)}>
            Apply
          </Button>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  attachGoCardlessCustomerToBooking,
  attachGoCardlessInvitationToBooking
};

export default connect(null, mapDispatchToProps)(Suggested);
